import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
//https://mui.com/components/tree-view/#contentcomponent-prop
import { cx } from '@emotion/css';
import { readableTypeName } from '@iotv/core-services';
import { getParentEdgeTypeId } from '@iotv/datamodel';
import { IconButton } from '@mui/material';
import { TreeItem, TreeItem2IconContainer, useTreeItemState } from '@mui/x-tree-view';
import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { DeleteDialog } from '../../../../ArbitrationObjects/DeletionArbitrator/DeleteDialog';
import { deletionArbitrator } from '../../../../ArbitrationObjects/DeletionArbitrator/deletionArbitrator';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { ActionIcons } from '../../../../Iconography';
import useQuestion from '../../../../Popup/useQuestion';
import { DialogWrapper } from '../../../../components/common/DiaglogWrapper';
import { setDefaultValues } from '../../../../components/factories/AttributeDefinitions';
import { mutateListerlizerTransactionFns } from '../../../../components/factories/Listerlizer/fns/mutateTransactionFns';
import Assembler from '../../../../data/Assembler';
import { isAdminRole } from '../../../../helpers/access';
import { useMostProximateCustomer } from '../../../../hooks/useMostProximateCustomer';
import { AdjacentType } from '../../../../types/AppTypes';
import { getViewDefinitionForNewObject } from '../../ExceptionTypesHandling/viewDefinitions';
import { ObjectDetailCard } from '../ObjectView/ObjectDetailCard';
import { LeafContextMenu } from './LeafContextMenu';
import { CustomContentRoot } from './cosmetics/CustomRootContent';
import { standardDeleteItem } from './standardTransactionVariantFns';
const debug = process.env.REACT_APP_DEBUG && false;
/** The actual row representing one single tree item. Includes context menu */
const CustomContent = React.forwardRef((props, ref) => {
    'use no memo';
    // Modifying component props or hook arguments is not allowed. Consider using a local variable instead.
    // `contextObject` cannot be modified
    const { className, label, itemId, icon: iconProp, expansionIcon, displayIcon, contextMenuComponentMap } = props;
    const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection } = useTreeItemState(itemId);
    const { contextObject, /*contextCustomer,*/ maintree_rightclick, x } = props;
    const icon = iconProp || expansionIcon || displayIcon;
    let adjacentFilter = undefined;
    const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState(null);
    const [contextMenuContextObject, setContextMenuContextObject] = useState();
    const [newObject, setNewObject] = useState(undefined);
    const [canDelete, setCanDelete] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [openDeleteGroup, setOpenDeleteGroup] = useState(false);
    const [resolve, setResolve] = useState(() => () => { });
    const [confirmation, setConfirmation] = useState({ title: 'Confirm?', question: 'Are you sure?' });
    const { Dialogue, setOpen } = useQuestion({ ...confirmation, result: (r) => resolve(r) });
    const getConfirmation = (props) => {
        setConfirmation(props);
        setOpen(true);
        return new Promise((resolve) => setResolve(() => resolve));
    };
    const mostProximateCustomer = useMostProximateCustomer({ item: contextObject });
    const [{ TransactionFunctions: transactionFunctions, UserFunctions, contextUser, applicationData, userSelectedGroup, roleArbitrationInstance, contextCustomer },] = useContext(AppMainViewContext);
    const handleCloseDeleteGroup = () => {
        setOpenDeleteGroup(false);
    };
    const handleMouseDown = (event) => {
        preventSelection(event);
    };
    const handleClick = (event) => {
        handleExpansion(event);
        handleSelection(event);
    };
    /** Runs when context menu is opened via button or right click **/
    const handleContextMenu = (event) => {
        debug && console.log(`ExtendedTreeItemWithContextMenu handleContextMenu`, { contextObject, event });
        event.stopPropagation();
        event.preventDefault();
        if (contextObject) {
            setContextMenuContextObject(contextObject);
            setContextMenuAnchorEl(event.currentTarget);
        }
    };
    const closeContextMenu = () => {
        setContextMenuAnchorEl(null);
    };
    const handleDeleteLocalObject = async () => {
        const localObject = contextMenuContextObject;
        if (contextUser) {
            const res = await standardDeleteItem({ item: localObject, contextUser, mostProximateCustomer, UserFunctions, getConfirmation });
            setOpenDeleteGroup(false);
        }
    };
    useEffect(() => {
        if (!contextObject?.type)
            return;
        if (contextObject?.type !== 'Group')
            return;
        const edgeTypeId = getParentEdgeTypeId(contextObject.supportedType, contextObject.type);
        if (!edgeTypeId)
            console.log(`WARN: cannot resolve edgeTyepId between child ${contextObject.supportedType} and parent ${contextObject.type}`);
        adjacentFilter = {
            adjacentType: AdjacentType.CHILD,
            edgeFilter: {
                edgeTypeId: edgeTypeId ?? 'unresolved',
            },
            objectType: contextObject.supportedType,
            otherObjectType: undefined,
        };
    }, [contextObject?.type]);
    /** Add item function. Opens a detail card where a new object can be made **/
    const handleContextMenuItem = (type) => {
        debug && console.log('ExtendedTreeItemV3', 'handleContextMenuItem', type);
        if (!contextObject)
            return;
        const newObject = Assembler.getInstance({ type });
        /** Group predicate */
        if (contextObject.type === 'Group' && newObject.type === 'Group') {
            newObject.supportedType = contextObject.supportedType;
        }
        if (contextObject.type === 'Group' && newObject.type === 'Device') {
            ;
            newObject.deviceType = contextObject.subSupportedType;
        }
        setDefaultValues(newObject, getViewDefinitionForNewObject(newObject) ?? {});
        setNewObject(newObject);
        setContextMenuAnchorEl(null);
    };
    /** Function to add a group. Run when we click an 'add group' action in the menu **/
    const handleContextMenuGroup = () => {
        debug && console.log('handleContextMenuGroup', contextObject?.type);
        if (!contextObject)
            return;
        const newObject = Assembler.getInstance({ type: 'Group' });
        const edgeTypeId = getParentEdgeTypeId(newObject.type, contextObject.type);
        if (edgeTypeId) {
            adjacentFilter = {
                adjacentType: AdjacentType.CHILD,
                edgeFilter: {
                    edgeTypeId: edgeTypeId,
                },
                objectType: 'Group',
                otherObjectType: undefined,
            };
        }
        /** Group predicate */
        newObject.supportedType = contextObject.supportedType ?? contextObject.type;
        setDefaultValues(newObject, getViewDefinitionForNewObject(newObject) ?? {});
        setNewObject(newObject);
        setContextMenuAnchorEl(null);
    };
    /** Function that handles deletion from the context menu */
    const handleContextMenuDelete = async () => {
        debug && console.log('ExtendedTreeItemV3', 'handleContextMenuDelete');
        if (!contextUser || !contextMenuContextObject)
            return;
        setContextMenuAnchorEl(null);
        const resultlist = applicationData && (await deletionArbitrator({ matchedPrimary: contextMenuContextObject, contextUser }));
        if (resultlist) {
            const { deletable, message } = resultlist;
            setCanDelete(deletable);
            setWarningMessage(message);
            setOpenDeleteGroup(true);
        }
    };
    const postTransactionCallback = async (errDataPromise) => {
        const transactionRes = await errDataPromise;
        if (transactionRes.err) {
            debug && console.log('ExtendedTreeItemWithContextMenu received unsuccessful result from DetailCard', transactionRes.err.message);
        }
        if (transactionRes.data) {
            debug && console.log('ExtendedTreeItemWithContextMenu received successful result from DetailCard', transactionRes.data);
            setNewObject(undefined);
        }
    };
    const mutatedTransactionFunctions = useMemo(() => {
        const constructedAdjacentFilter = {
            objectType: (newObject?.type ?? ''),
            edgeFilter: {
                edgeTypeId: getParentEdgeTypeId(newObject?.type ?? '', contextObject?.type ?? '') ?? '',
            },
            adjacentType: AdjacentType.CHILD,
        };
        debug && console.log('ExtendedTreeItemV3', 'mutated context menu transaction fns');
        return mutateListerlizerTransactionFns({
            transactionFunctions,
            userFunctions: UserFunctions,
            resolvedContextObject: contextObject,
            newObject,
            adjacentFilter: adjacentFilter ?? constructedAdjacentFilter,
            contextUser,
            contextCustomer,
            mostProximateCustomer,
            setNewObject,
            getConfirmation,
        });
    }, [newObject, contextObject, adjacentFilter]);
    const contextMenuProps = useMemo(() => roleArbitrationInstance
        ? {
            roleArbitrationInstance: roleArbitrationInstance,
            userSelectedGroup: userSelectedGroup,
            anchorEl: contextMenuAnchorEl,
            contextObject: contextMenuContextObject,
            handleClose: closeContextMenu,
            handleItem: handleContextMenuItem,
            handleGroup: handleContextMenuGroup,
            handleDelete: handleContextMenuDelete,
        }
        : undefined, [roleArbitrationInstance, userSelectedGroup, contextMenuAnchorEl]);
    const contextObjectType = contextObject?.type; // Idk why but I need to extract context object.type into it's own var or the line below errors
    const canAccessContextMenu = contextObjectType && contextMenuComponentMap?.[contextObjectType]?.roleFilter !== undefined
        ? contextMenuComponentMap[contextObjectType].roleFilter(userSelectedGroup)
        : isAdminRole(userSelectedGroup);
    return (_jsxs(Fragment, { children: [_jsxs(CustomContentRoot, { ref: ref, className: cx(className, { 'Mui-expanded': expanded, 'Mui-selected': selected, 'Mui-focused': focused, 'Mui-disabled': disabled }),
                onClick: handleClick,
                onMouseDown: handleMouseDown,
                onContextMenu: handleContextMenu,
                sx: { display: 'flex', alignItems: 'center' }, children: [_jsx(TreeItem2IconContainer, { children: icon }), label, canAccessContextMenu && maintree_rightclick !== undefined && maintree_rightclick && (_jsx(IconButton, { onClick: handleContextMenu, size: "small", style: { margin: 0, padding: 3 }, children: _jsx(ActionIcons.ContextMenu, { fontSize: "inherit" }) })), x, contextMenuProps &&
                        contextObject &&
                        maintree_rightclick !== undefined &&
                        maintree_rightclick &&
                        (contextMenuComponentMap?.[contextObject.type] ? (contextMenuComponentMap[contextObject.type].component(contextMenuProps)) : (_jsx(LeafContextMenu, { ...contextMenuProps })))] }), _jsx(DialogWrapper, { header: `Add ${newObject?.type ? readableTypeName(newObject.type) : 'Entity'} to ${contextObject?.name}`,
                open: newObject !== undefined,
                setClosed: (actioned) => {
                    debug && console.log('ExtendedTreeItemWithContextMenu: something returned from dialog', actioned);
                    setNewObject(undefined);
                }, children: newObject ? (_jsx(ObjectDetailCard, { viewDefinition: getViewDefinitionForNewObject(newObject),
                    matchedPrimary: newObject,
                    contextObjectForDetailCard: contextObject,
                    mostProximateCustomer,
                    subContextCustomer: mostProximateCustomer,
                    postTransactionCallback,
                    userFunctions: UserFunctions,
                    transactionFunctions: mutatedTransactionFunctions,
                    allowedActions: contextObjectType
                        ? roleArbitrationInstance?.getObjectControlActionsByType({ sourceObjectType: contextObjectType, targetObjectType: newObject.type })
                        : roleArbitrationInstance?.getSingleObjectControlActions(newObject.type) })) : (_jsx(_Fragment, {})) }, `${itemId}_newObjectDialog`), DeleteDialog({
                handleClose: handleCloseDeleteGroup,
                handleDelete: handleDeleteLocalObject,
                open: openDeleteGroup,
                warningMessage,
                candelete: canDelete,
            }), Dialogue] }));
});
export const ExtendedTreeItem = React.forwardRef(({ keyIn, contextObject, ...props }, ref) => {
    /** contextObject spread out to get rid of hook.js:608 Warning: React does not recognize the `contextObject` prop on a DOM element. */
    /** 2024 12 appears to be source of warnings like warning: validateDOMNesting(...): <div> cannot appear as a descendant of <p>. Error Component Stack */
    /** CANARY changing to TreeItem2 breaks Coverage entry in the main menu under Properties, with the text field appearing blank */
    const { contextMenuComponentMap, ...otherExtraProps } = props;
    return (_jsx(TreeItem, { ref: ref, itemId: keyIn, 
        // @ts-expect-error `TreeItemContentProps` is missing `x`, `status`
        ContentComponent: CustomContent, ContentProps: { contextObject, contextMenuComponentMap, ...otherExtraProps }, ...otherExtraProps }, keyIn));
    // TODO: migrate to TreeItem2
    // return (
    //   <TreeItem itemId={keyIn} {...props} slots={{ content: CustomContent }} slotProps={{ content: props as any }} />
    // );
});
